import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { RECALL_KINDS, RECALL_SEND_CHANNELS } from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { recallKindLabel, recallStatusWord, recallWithheldWord } from '../status-labels.js'

/**
 * The recall queue a shopkeeper works through. §6.24 `APT-004` to `APT-006`.
 *
 * `GET /recalls` has existed and nothing on the counter read it, so a campaign run produced a queue
 * nobody could see. Asking a customer back for a sight test or a contact lens resupply is revenue
 * the shop has already earned the right to ask for, and it was sitting in a table.
 *
 * **`lapsed` is a safety flag, not a reminder.** `APT-006` says so: a contact lens wearer whose
 * supply ran out before today is wearing lenses they have run out of. It is the server comparing
 * `supply_ends_on` with its own clock, so it arrives on the list and never on a write response.
 *
 * **A withheld entry is shown with its reason.** `APT-005` keeps the row rather than skipping it,
 * because "we did not contact this customer and here is why" is the only question anybody asks
 * about a recall that never arrived. So `withheld` is in the queue and the reason is on the row.
 *
 * **The channel is asked for, never assumed.** `RECALL_SEND_CHANNELS` is email, in_app and
 * by_hand, and `MSG-001a` makes `by_hand` a phone call: the shop records which of the three it
 * actually did, and Appendix Z's recall response report counts against it. A default would make
 * every phone call look like an email.
 *
 * **`RECALL_TRANSITIONS` decides which buttons a row gets**, and the edge worth naming is
 * `sent -> converted` without `responded`: a customer can buy without answering first, and often
 * does, because they walk in.
 */

export interface RecallEntryData {
  readonly id: string
  readonly customerId: string
  /** `APT-004`'s four rules: sight test, contact lens, frame refresh, warranty expiry. */
  readonly kind: string
  readonly status: string
  readonly dueOn: string
  readonly sendOn: string
  /** `RECALL_KINDS_THAT_NUDGE`: a contact lens recall nudges more than once. */
  readonly nudgeNumber: number
  readonly version: number
  /** `APT-006`: the supply ran out before today, which the spec calls a safety issue. */
  readonly lapsed: boolean
  readonly supplyEndsOn?: string
  readonly channel?: string
  readonly withheldReason?: string
  readonly sourceOrderId?: string
  readonly convertedOrderId?: string
}

/**
 * The kinds a run is asked for. `warranty_expiry` is left out: nothing records when a warranty ends,
 * so the engine always skips it and asking would produce a skipped row and a confused shopkeeper.
 */
const RUNNABLE_KINDS = RECALL_KINDS.filter((kind) => kind !== 'warranty_expiry')

/** What a run answered, reduced to the three numbers a shopkeeper reads. */
interface RunSummary {
  readonly found: number
  readonly alreadyQueued: number
  readonly skipped: number
}

const countOf = (value: unknown): number => (typeof value === 'number' ? value : 0)

function summaryOf(response: unknown): RunSummary {
  const body =
    typeof response === 'object' && response !== null ? (response as Record<string, unknown>) : {}
  return {
    found: countOf(body['dueCount']) + countOf(body['withheldCount']),
    alreadyQueued: countOf(body['existingCount']),
    skipped: Array.isArray(body['skipped']) ? body['skipped'].length : 0,
  }
}

export interface RecallsProps {
  /**
   * The branch a run is for. `POST /recalls/runs` requires one. Without it the run button is
   * absent, not disabled: a button that cannot do anything is worse than none.
   */
  readonly branchId?: string
  /**
   * The branch's timezone, for `LOC-004`.
   *
   * Appendix J: an instant renders in the branch's zone, never the browser's. A plain `date`
   * column renders in none, which is what `formatDay` is for.
   */
  readonly timezone: string
  readonly load: (status?: string) => Promise<readonly RecallEntryData[]>
  readonly onOpenCustomer?: (customerId: string) => void
}

export function Recalls({ branchId, load, onOpenCustomer, timezone }: RecallsProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  /** Blank is the server's own open set, which is the queue. A status is looking back. */
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<readonly RecallEntryData[] | null>(null)
  const [channels, setChannels] = useState<Record<string, string>>({})
  /** The form's own complaint (no channel chosen). Never a reason to replace the list. */
  const [error, setError] = useState<string | null>(null)
  /** A failed read, kept apart from `error` so a missing channel cannot blank the queue. */
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  /** The id of the run write, so a settled mark-sent is not read as a run's answer. */
  const runWriteId = useRef<string | null>(null)
  const [runSummary, setRunSummary] = useState<RunSummary | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load(status === '' ? undefined : status))
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : t('recalls.loadFailed'))
    }
  }, [load, status, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, (write) => {
    if (write.id === runWriteId.current) {
      runWriteId.current = null
      setRunSummary(summaryOf(write.response))
    }
    setWriteId(null)
    void refresh()
  })

  /**
   * `APT-004`: work out who is due and queue them. One run in flight at a time, because a second
   * tap on a slow connection would queue the same run twice behind the first.
   */
  const runInFlight = snapshot.writes.some(
    (write) =>
      write.id === runWriteId.current && (write.status === 'pending' || write.status === 'sending'),
  )
  const runCampaign = useCallback(async () => {
    if (branchId === undefined) return
    setError(null)
    setRunSummary(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/recalls/runs',
      label: t('recalls.queueRunLabel'),
      body: { branchId, kinds: RUNNABLE_KINDS },
    })) as { id: string }
    runWriteId.current = write.id
    setWriteId(write.id)
  }, [branchId, enqueue, t])

  const markSent = useCallback(
    async (row: RecallEntryData) => {
      const channel = channels[row.id]
      if (channel === undefined || channel === '') {
        setError(t('recalls.channelRequired'))
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/recalls/${row.id}/sent`,
        label: t('recalls.queueSentLabel'),
        // `ARC-007`: the version this screen read, so two people working the same queue is a
        // conflict rather than one contact recorded twice.
        body: { channel, expectedVersion: row.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [channels, enqueue, t],
  )

  const recordOutcome = useCallback(
    async (row: RecallEntryData, outcome: 'responded' | 'converted') => {
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/recalls/${row.id}/outcome`,
        label: t('recalls.queueOutcomeLabel'),
        body: { outcome, expectedVersion: row.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.recall'))

  return (
    <section className="lm-screen" aria-labelledby="recalls-heading">
      <h1 id="recalls-heading" className="lm-screen__heading">
        {t('recalls.heading')}
      </h1>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('recalls.statusLabel')}</span>
        <select
          className="lm-input"
          value={status}
          onChange={(e) => {
            setStatus(e.currentTarget.value)
          }}
        >
          {/* Blank first, because the server's open set is the queue and a status is looking back
              at something already dealt with. */}
          <option value="">{t('recalls.statusOpen')}</option>
          <option value="sent">{t('recalls.statusSent')}</option>
          <option value="withheld">{t('recalls.statusWithheld')}</option>
          <option value="responded">{t('recalls.statusResponded')}</option>
          <option value="converted">{t('recalls.statusConverted')}</option>
          <option value="expired">{t('recalls.statusExpired')}</option>
        </select>
      </label>

      {branchId === undefined ? null : (
        <div className="lm-recall-run">
          <button
            type="button"
            className="lm-button lm-button--secondary"
            disabled={runInFlight}
            onClick={() => void runCampaign()}
          >
            {t('recalls.run')}
          </button>
          {runSummary === null ? null : (
            // A run that found nobody is the ordinary answer on a shop whose reminders are off, so
            // it is a status line and says what to check, not an alert.
            <p className="lm-hint" role="status">
              {runSummary.found === 0
                ? t('recalls.runNone', { skipped: runSummary.skipped })
                : t('recalls.runResult', {
                    found: runSummary.found,
                    alreadyQueued: runSummary.alreadyQueued,
                    skipped: runSummary.skipped,
                  })}
            </p>
          )}
        </div>
      )}

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        Blank status is the server's open set, so nothing there is first-run. A chosen status that
        matches nothing is a filter that matched nothing, which is different news.
      */}
      <DataSurface
        state={
          loadError !== null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  setRows(null)
                  void refresh()
                },
              }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: status === '' ? 'empty' : 'filtered' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('recalls.what')}
        emptyMessage={t('recalls.empty')}
        filteredMessage={t('recalls.filtered')}
      >
        <ul className="lm-recall-queue">
          {(rows ?? []).map((row) => (
            <li
              key={row.id}
              className="lm-recall-entry"
              data-lapsed={row.lapsed ? 'yes' : undefined}
            >
              <span className="lm-recall-entry__kind">{recallKindLabel(t, row.kind)}</span>
              <span className="lm-recall-entry__status">{recallStatusWord(t, row.status)}</span>
              <span className="lm-recall-entry__when">
                {sentence(t, 'recalls.due', {
                  date: <Ltr>{formatDay(row.dueOn, timezone, locale)}</Ltr>,
                })}
              </span>
              {row.nudgeNumber > 1 ? (
                // `RECALL_KINDS_THAT_NUDGE`: a contact lens recall asks more than once, and which
                // ask this is changes what a shopkeeper says on the phone.
                <span className="lm-recall-entry__nudge">
                  {t('recalls.nudge', { number: row.nudgeNumber })}
                </span>
              ) : null}

              {row.lapsed ? (
                <span className="lm-recall-entry__lapsed" role="alert">
                  {/* `APT-006` calls this a safety issue, not a reminder: the customer has run out
                      of lenses they are still wearing. */}
                  {row.supplyEndsOn === undefined
                    ? t('recalls.lapsed')
                    : t('recalls.lapsedOn', {
                        date: formatDay(row.supplyEndsOn, timezone, locale),
                      })}
                </span>
              ) : null}

              {row.withheldReason === undefined ? null : (
                <span className="lm-recall-entry__withheld">
                  {t('recalls.withheldBecause', {
                    reason: recallWithheldWord(t, row.withheldReason),
                  })}
                </span>
              )}

              {onOpenCustomer === undefined ? null : (
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    onOpenCustomer(row.customerId)
                  }}
                >
                  {t('recalls.openCustomer')}
                </button>
              )}

              {/* `due -> sent` and `withheld -> sent`, both of which `RECALL_TRANSITIONS` allows.
                  The channel is a field because the shop records what it actually did. */}
              {row.status === 'due' || row.status === 'withheld' ? (
                <>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('recalls.channelLabel', { kind: recallKindLabel(t, row.kind) })}
                    </span>
                    <select
                      className="lm-input"
                      value={channels[row.id] ?? ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        setChannels((previous) => ({ ...previous, [row.id]: value }))
                      }}
                    >
                      <option value="">{t('recalls.channelUnset')}</option>
                      {RECALL_SEND_CHANNELS.map((channel) => (
                        <option key={channel} value={channel}>
                          {t(
                            channel === 'email'
                              ? 'recalls.channelEmail'
                              : channel === 'in_app'
                                ? 'recalls.channelInApp'
                                : 'recalls.channelByHand',
                          )}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => void markSent(row)}
                  >
                    {t('recalls.markSent')}
                  </button>
                </>
              ) : null}

              {/* `sent -> responded` and `sent -> converted`. Both offered, because a customer can
                  buy without answering first and often does: they walk in. */}
              {row.status === 'sent' ? (
                <>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => void recordOutcome(row, 'responded')}
                  >
                    {t('recalls.markResponded')}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => void recordOutcome(row, 'converted')}
                  >
                    {t('recalls.markConverted')}
                  </button>
                </>
              ) : null}

              {/* `responded -> converted` is the only edge left. */}
              {row.status === 'responded' ? (
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => void recordOutcome(row, 'converted')}
                >
                  {t('recalls.markConverted')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </DataSurface>
    </section>
  )
}
